import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Moon, RefreshCw } from 'lucide-react';
import { checkAvailability, type Availability } from '../../services/availability.service';
import { localizePath, normalizeLanguage } from '../../utils/languageRouting';

export const MaintenanceNotice = () => {
  const { t, i18n } = useTranslation('maintenance');
  const [status, setStatus] = useState<Availability>({ mode: 'online' });
  const [checking, setChecking] = useState(false);
  const request = useRef<AbortController | null>(null);
  const mounted = useRef(false);

  const check = useCallback(async () => {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setChecking(true);
    const timeout = setTimeout(() => controller.abort(), 6000);
    try {
      const result = await checkAvailability(controller.signal);
      if (mounted.current) setStatus(result);
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) request.current = null;
      if (mounted.current) setChecking(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (navigator.userAgent.includes('ReactSnap')) return () => { mounted.current = false; };
    const preview = import.meta.env.DEV ? new URLSearchParams(location.search).get('previewMaintenance') : null;
    if (preview === 'paused' || preview === 'unavailable') {
      setStatus({ mode: preview, resumeAt: new Date(Date.now() + 3_600_000).toISOString() });
      return () => { mounted.current = false; };
    }
    void check();
    const onVisible = () => { if (!document.hidden) void check(); };
    const timer = setInterval(onVisible, 60_000);
    window.addEventListener('online', onVisible);
    window.addEventListener('offline', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      request.current?.abort();
      request.current = null;
      window.removeEventListener('online', onVisible);
      window.removeEventListener('offline', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  if (status.mode === 'online') return null;
  const time = status.resumeAt ? new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
    timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit',
  }).format(new Date(status.resumeAt)) : '06:30';

  return (
    <aside className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3 text-teal-950 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-50" aria-label={t('title')}>
      <Moon className="h-5 w-5 shrink-0 text-teal-700 dark:text-teal-300" aria-hidden="true" />
      <div className="min-w-0 flex-1" role="status" aria-live="polite">
        <p className="text-sm font-semibold">{t('title')}</p>
        <p className="text-sm leading-relaxed">{t(status.mode, { time })}</p>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <Link className="rounded underline underline-offset-4 focus-visible:outline focus-visible:outline-2" to={localizePath('/guides', normalizeLanguage(i18n.language))}>{t('guides')}</Link>
        <button type="button" disabled={checking} onClick={() => void check()} className="inline-flex items-center gap-1 rounded-lg px-2 py-2 font-medium hover:bg-teal-100 focus-visible:outline focus-visible:outline-2 disabled:opacity-60 dark:hover:bg-teal-900">
          <RefreshCw className={`h-3.5 w-3.5 ${checking ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
          {t(checking ? 'checking' : 'retry')}
        </button>
      </div>
    </aside>
  );
};
